import React, { useEffect, useState, useCallback, useRef } from "react";
import { db } from "../../firebase";
import { collection, getDocs, getDoc, doc, setDoc, updateDoc, deleteDoc, serverTimestamp, increment } from "firebase/firestore";
import { GoogleMap, useJsApiLoader, Polygon, DrawingManager } from "@react-google-maps/api";
import Modal from "../../components/Modal";
import { MapIcon, Satellite, MousePointerClick, AlertTriangle, Trash2, Upload, Download, FileText, Pencil, Search, X } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { GOOGLE_MAPS_KEY, GOOGLE_MAPS_LIBRARIES } from "../../utils/googleMapsConfig";
import { getMapCenter } from "../../utils/timezoneCoords";

const ZONE_COLORS = [
  // Blues
  "#1A56A0", "#2563EB", "#0EA5E9", "#06B6D4",
  // Greens
  "#38A169", "#16A34A", "#84CC16", "#65A30D",
  // Reds & Pinks
  "#E53E3E", "#DC2626", "#E91E8C", "#DB2777",
  // Purples & Indigos
  "#805AD5", "#7C3AED", "#6366F1", "#4F46E5",
  // Oranges & Yellows
  "#DD6B20", "#EA580C", "#D97706", "#CA8A04",
  // Teals & Grays
  "#319795", "#0F766E", "#475569", "#1E293B",
];

function SectionsManager({ companyId }) {
  const { t } = useLanguage();
  const [sections, setSections]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [editingSection, setEditing]      = useState(null);
  const [error, setError]                 = useState("");
  const [success, setSuccess]             = useState("");
  const [saving, setSaving]               = useState(false);
  const [deleting, setDeleting]           = useState(false);
  const [drawnCoords, setDrawnCoords]     = useState([]);
  const [mapMode, setMapMode]             = useState("view");   // "view" | "draw" | "edit"
  const [mapType, setMapType]             = useState("roadmap");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showForm, setShowForm]           = useState(false);
  const [searchQuery, setSearchQuery]     = useState("");

  const drawingManagerRef = useRef(null);
  const importFileRef     = useRef(null);
  const editPolygonRef    = useRef(null);
  const drawnPolygonRef   = useRef(null);
  const mapRef            = useRef(null);

  const [importing, setImporting] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 25.2048, lng: 55.2708 });
  const [mapZoom,   setMapZoom]   = useState(11);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const emptyForm = { name: "", color: ZONE_COLORS[0], description: "" };
  const [form, setForm] = useState(emptyForm);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSections(); }, []);

  useEffect(() => {
    if (!companyId) return;
    async function loadMapCenter() {
      try {
        const snap     = await getDoc(doc(db, "companies", companyId, "config", "settings"));
        const timezone = snap.exists() ? (snap.data().timezone || "Asia/Dubai") : "Asia/Dubai";
        const center   = getMapCenter(timezone);
        setMapCenter({ lat: center.lat, lng: center.lng });
        setMapZoom(center.zoom || 11);
      } catch (err) {
        console.error("[MAP] Failed to load map center:", err.message);
      }
    }
    loadMapCenter();
  }, [companyId]);

  async function loadSections() {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "companies", companyId, "sections"));
      setSections(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError("Failed to load sections: " + err.message);
    }
    setLoading(false);
  }

  function flash(msg, isError = false) {
    if (isError) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(""); setSuccess(""); }, 3000);
  }

  function startDrawing() {
    setEditing(null);
    setForm(emptyForm);
    setDrawnCoords([]);
    setShowForm(false);
    editPolygonRef.current = null;
    drawnPolygonRef.current = null;
    setMapMode("draw");
  }

  function startEdit(section) {
    setEditing(section);
    setForm({
      name:        section.name        || "",
      color:       section.color       || ZONE_COLORS[0],
      description: section.description || "",
    });
    setDrawnCoords(section.polygon || []);
    editPolygonRef.current = null;
    drawnPolygonRef.current = null;
    setMapMode("edit");
    setShowForm(true);
    if (mapRef.current && section.polygon?.length > 0 && window.google) {
      const bounds = new window.google.maps.LatLngBounds();
      section.polygon.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
      mapRef.current.fitBounds(bounds, 80);
    }
  }

  function cancelAction() {
    setMapMode("view");
    setEditing(null);
    setForm(emptyForm);
    setDrawnCoords([]);
    setShowForm(false);
    editPolygonRef.current = null;
    drawnPolygonRef.current = null;
  }

  const onPolygonComplete = useCallback((polygon) => {
    const path = polygon.getPath().getArray().map(p => ({ lat: p.lat(), lng: p.lng() }));
    polygon.setMap(null);
    setDrawnCoords(path);
    setMapMode("view");
    setShowForm(true);
  }, []);

  async function handleSave(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError("Zone name is required."); return; }

    let finalCoords = drawnCoords;
    if (editingSection && editPolygonRef.current) {
      finalCoords = editPolygonRef.current.getPath().getArray().map(p => ({ lat: p.lat(), lng: p.lng() }));
    } else if (!editingSection && drawnPolygonRef.current) {
      finalCoords = drawnPolygonRef.current.getPath().getArray().map(p => ({ lat: p.lat(), lng: p.lng() }));
    }

    if (finalCoords.length < 3) { setError("Please draw a zone on the map (at least 3 points)."); return; }
    setError("");
    setSaving(true);
    try {
      const data = {
        name:        form.name.trim(),
        color:       form.color,
        description: form.description.trim(),
        polygon:     finalCoords,
        updated_at:  serverTimestamp(),
      };
      if (editingSection) {
        await updateDoc(doc(db, "companies", companyId, "sections", editingSection.id), data);
        setSections(prev => prev.map(s => s.id === editingSection.id ? { ...s, ...data } : s));
        flash("✅ Zone updated!");
      } else {
        const sectionId = "section-" + Date.now().toString(36);
        data.created_at = serverTimestamp();
        await setDoc(doc(db, "companies", companyId, "sections", sectionId), data);
        try {
          await updateDoc(doc(db, "companies", companyId), { section_count: increment(1) });
        } catch (e) { console.error("[COUNTER] section_count increment failed:", e.message); }
        setSections(prev => [...prev, { id: sectionId, ...data }]);
        flash("✅ Zone created!");
      }
      cancelAction();
    } catch (err) {
      setError("Error: " + err.message);
    }
    setSaving(false);
  }

  async function handleDelete(sectionId) {
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "companies", companyId, "sections", sectionId));
      try {
        await updateDoc(doc(db, "companies", companyId), { section_count: increment(-1) });
      } catch (e) { console.error("[COUNTER] section_count decrement failed:", e.message); }
      setSections(prev => prev.filter(s => s.id !== sectionId));
      setConfirmDelete(null);
      flash("✅ Zone deleted.");
    } catch (err) {
      flash("Error: " + err.message, true);
    }
    setDeleting(false);
  }

  // ── EXPORT ──────────────────────────────────────────────────
  function handleDownloadGeoJSON() {
    const geojson = {
      type: "FeatureCollection",
      features: sections
        .filter(s => s.polygon?.length > 2)
        .map(s => ({
          type: "Feature",
          properties: { id: s.id, name: s.name, color: s.color, description: s.description || "" },
          geometry: {
            type: "Polygon",
            coordinates: [[...s.polygon.map(p => [p.lng, p.lat]), [s.polygon[0].lng, s.polygon[0].lat]]],
          },
        })),
    };
    const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: "application/json" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = "zones.geojson";
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleDownloadKML() {
    const placemarks = sections
      .filter(s => s.polygon?.length > 2)
      .map(s => {
        const coords = [...s.polygon, s.polygon[0]]
          .map(p => `${p.lng},${p.lat},0`)
          .join(" ");
        return `  <Placemark>
    <name>${escapeXml(s.name)}</name>
    <description>${escapeXml(s.description || "")}</description>
    <Style><PolyStyle><color>7f${s.color.replace("#","").match(/.{2}/g).reverse().join("")}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>
    <Polygon><outerBoundaryIs><LinearRing><coordinates>${coords}</coordinates></LinearRing></outerBoundaryIs></Polygon>
  </Placemark>`;
      }).join("\n");

    const kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
<Document>
  <name>Zones</name>
${placemarks}
</Document>
</kml>`;
    const blob = new Blob([kml], { type: "application/vnd.google-earth.kml+xml" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = "zones.kml";
    a.click();
    URL.revokeObjectURL(url);
  }

  function escapeXml(str) {
    return String(str || "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  }

  // ── IMPORT ──────────────────────────────────────────────────
  async function handleImportFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setImporting(true);
    setError("");
    try {
      const text = await file.text();
      let zones = [];

      if (file.name.endsWith(".geojson") || file.name.endsWith(".json")) {
        const gj = JSON.parse(text);
        const features = gj.type === "FeatureCollection" ? gj.features : [gj];
        for (const f of features) {
          if (f.geometry?.type !== "Polygon") continue;
          const coords = f.geometry.coordinates[0];
          const polygon = coords.slice(0, -1).map(c => ({ lat: c[1], lng: c[0] }));
          if (polygon.length < 3) continue;
          zones.push({
            name:        f.properties?.name || "Imported Zone",
            color:       f.properties?.color || ZONE_COLORS[0],
            description: f.properties?.description || "",
            polygon,
          });
        }
      } else if (file.name.endsWith(".kml")) {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, "text/xml");
        const placemarks = xmlDoc.getElementsByTagName("Placemark");
        for (const pm of placemarks) {
          const name = pm.getElementsByTagName("name")[0]?.textContent || "Imported Zone";
          const desc = pm.getElementsByTagName("description")[0]?.textContent || "";
          const coordsEl = pm.getElementsByTagName("coordinates")[0];
          if (!coordsEl) continue;
          const polygon = coordsEl.textContent.trim().split(/\s+/).map(c => {
            const [lng, lat] = c.split(",").map(Number);
            return { lat, lng };
          }).filter(p => !isNaN(p.lat) && !isNaN(p.lng));
          if (polygon.length < 3) continue;
          const last = polygon[polygon.length - 1];
          if (last.lat === polygon[0].lat && last.lng === polygon[0].lng) polygon.pop();
          zones.push({ name, color: ZONE_COLORS[0], description: desc, polygon });
        }
      } else {
        throw new Error("Unsupported file type. Please upload a .geojson or .kml file.");
      }

      if (zones.length === 0) throw new Error("No valid polygon zones found in file.");

      let created = 0;
      for (const z of zones) {
        const sectionId = "section-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2,6);
        const data = { ...z, created_at: serverTimestamp(), updated_at: serverTimestamp() };
        await setDoc(doc(db, "companies", companyId, "sections", sectionId), data);
        try {
          await updateDoc(doc(db, "companies", companyId), { section_count: increment(1) });
        } catch (e) { /* non-critical */ }
        setSections(prev => [...prev, { id: sectionId, ...z }]);
        created++;
      }

      flash(`✅ Imported ${created} zone(s) successfully!`);
    } catch (err) {
      flash("Import error: " + err.message, true);
    }
    setImporting(false);
  }

  const filteredSections = sections.filter(s =>
    !searchQuery || s.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <p className="text-gray-500 text-sm">{t("sections_loading")}</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {error   && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">{success}</div>}

      {!isLoaded ? (
        <div className="bg-white rounded-xl shadow-sm flex items-center justify-center" style={{ height: "680px" }}>
          <p className="text-gray-500 text-sm">{t("sections_map_loading")}</p>
        </div>
      ) : (
        <div className="relative rounded-xl overflow-hidden shadow-lg" style={{ height: "680px" }}>

          {/* ── Full-size map ── */}
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={mapCenter}
            zoom={mapZoom}
            mapTypeId={mapType}
            onLoad={map => { mapRef.current = map; }}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              zoomControlOptions: { position: 7 },
            }}>

            {/* All zones except the one being edited */}
            {sections
              .filter(s => s.polygon?.length > 2 && (!editingSection || s.id !== editingSection.id))
              .map(section => (
                <Polygon key={section.id} paths={section.polygon}
                  options={{
                    fillColor: section.color || "#1A56A0",
                    fillOpacity: 0.25,
                    strokeColor: section.color || "#1A56A0",
                    strokeWeight: 2,
                  }} />
              ))}

            {/* Editable polygon for edit mode */}
            {mapMode === "edit" && drawnCoords.length > 2 && (
              <Polygon
                paths={drawnCoords}
                onLoad={poly => { editPolygonRef.current = poly; }}
                options={{
                  fillColor: form.color,
                  fillOpacity: 0.35,
                  strokeColor: form.color,
                  strokeWeight: 2.5,
                  editable: true,
                  draggable: true,
                }}
              />
            )}

            {/* Drawn polygon preview after completing a draw */}
            {mapMode === "view" && showForm && !editingSection && drawnCoords.length > 2 && (
              <Polygon
                paths={drawnCoords}
                onLoad={poly => { drawnPolygonRef.current = poly; }}
                options={{
                  fillColor: form.color,
                  fillOpacity: 0.35,
                  strokeColor: form.color,
                  strokeWeight: 2.5,
                  editable: true,
                  draggable: true,
                }}
              />
            )}

            {/* Drawing manager — only in draw mode */}
            {mapMode === "draw" && (
              <DrawingManager
                ref={drawingManagerRef}
                drawingMode="polygon"
                onPolygonComplete={onPolygonComplete}
                options={{
                  drawingControl: false,
                  polygonOptions: {
                    fillColor: form.color,
                    fillOpacity: 0.3,
                    strokeColor: form.color,
                    strokeWeight: 2,
                    editable: true,
                  },
                }}
              />
            )}
          </GoogleMap>

          {/* ── Top bar overlay (left side, stops before sidebar) ── */}
          <div className="absolute top-3 left-3 z-10 flex items-center gap-2 flex-wrap"
            style={{ right: "308px" }}>

            {/* Title card */}
            <div className="bg-white rounded-xl shadow-md px-4 py-2.5 flex items-center gap-2.5">
              <div>
                <p className="text-sm font-semibold text-gray-800 leading-tight">{t("sections_geofences")}</p>
                <p className="text-xs text-gray-400">{sections.length} {t("sections_zones_total")}</p>
              </div>
            </div>

            {/* Map type toggle */}
            <button
              onClick={() => setMapType(prev => prev === "roadmap" ? "satellite" : "roadmap")}
              className="bg-white shadow-md rounded-xl px-3 py-2.5 flex items-center gap-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 border border-gray-100 transition">
              {mapType === "roadmap"
                ? <><Satellite className="w-3.5 h-3.5" /> {t("sections_satellite")}</>
                : <><MapIcon className="w-3.5 h-3.5" /> {t("sections_map")}</>}
            </button>

            {/* Import */}
            <input ref={importFileRef} type="file" accept=".geojson,.json,.kml" className="hidden" onChange={handleImportFile} />
            <button
              onClick={() => importFileRef.current?.click()}
              disabled={importing}
              className="bg-white shadow-md rounded-xl px-3 py-2.5 flex items-center gap-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 border border-gray-100 transition disabled:opacity-50">
              <Upload className="w-3.5 h-3.5" />{importing ? t("sections_importing") : t("sections_import")}
            </button>

            {/* Export dropdown */}
            {sections.length > 0 && (
              <div className="relative group">
                <button className="bg-white shadow-md rounded-xl px-3 py-2.5 flex items-center gap-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 border border-gray-100 transition">
                  <Download className="w-3.5 h-3.5" /> {t("sections_export")}
                </button>
                <div className="absolute left-0 mt-1 w-36 bg-white border border-gray-200 rounded-xl shadow-lg z-30 hidden group-hover:block overflow-hidden">
                  <button onClick={handleDownloadGeoJSON}
                    className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                    <FileText className="w-3 h-3" /> GeoJSON
                  </button>
                  <button onClick={handleDownloadKML}
                    className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                    <FileText className="w-3 h-3" /> KML
                  </button>
                </div>
              </div>
            )}

            {/* Draw / Cancel drawing button */}
            {mapMode !== "draw" ? (
              <button
                onClick={startDrawing}
                disabled={mapMode === "edit"}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white shadow-md rounded-xl px-4 py-2.5 text-xs font-semibold flex items-center gap-1.5 transition">
                {t("sections_draw_new_zone")}
              </button>
            ) : (
              <button
                onClick={cancelAction}
                className="bg-red-500 hover:bg-red-600 text-white shadow-md rounded-xl px-4 py-2.5 text-xs font-semibold flex items-center gap-1.5 transition">
                <X className="w-3.5 h-3.5" /> {t("sections_cancel_drawing")}
              </button>
            )}
          </div>

          {/* ── Drawing hint bar (bottom center) ── */}
          {mapMode === "draw" && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
              <div className="bg-gray-900 bg-opacity-85 text-white text-xs font-medium px-5 py-2.5 rounded-full shadow-xl flex items-center gap-2">
                <MousePointerClick className="w-3.5 h-3.5" />
                {t("sections_draw_hint_msg")}
              </div>
            </div>
          )}

          {/* ── Form panel (floating, bottom-left) ── */}
          {showForm && (
            <div className="absolute bottom-4 left-4 z-20 bg-white rounded-2xl shadow-2xl overflow-hidden"
              style={{ width: "288px" }}>
              {/* Header */}
              <div className="px-4 py-3 border-b flex items-center justify-between"
                style={{ backgroundColor: form.color + "18", borderColor: form.color + "40" }}>
                <p className="text-sm font-bold text-gray-800">
                  {editingSection ? t("sections_edit_zone") : t("sections_new_zone")}
                </p>
                <button onClick={cancelAction} className="text-gray-400 hover:text-gray-600 transition p-0.5 rounded-lg hover:bg-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <form onSubmit={handleSave} className="p-4 space-y-3">
                {error && (
                  <p className="text-red-600 text-xs bg-red-50 border border-red-200 rounded-lg p-2">{error}</p>
                )}
                {/* Zone name */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">{t("sections_zone_name_label")}</label>
                  <input
                    autoFocus
                    value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    required
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="e.g. Zone A, Downtown"
                  />
                </div>
                {/* Color picker */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("sections_color_label")}</label>
                  <div className="grid grid-cols-8 gap-1">
                    {ZONE_COLORS.map(color => (
                      <button key={color} type="button" onClick={() => setForm(p => ({ ...p, color }))}
                        title={color}
                        style={{ backgroundColor: color }}
                        className={`w-6 h-6 rounded-full transition hover:scale-110 ${
                          form.color === color ? "ring-2 ring-offset-1 ring-gray-500 scale-110" : ""
                        }`} />
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <input type="color" value={form.color}
                      onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
                      className="w-6 h-6 rounded cursor-pointer border border-gray-200" />
                    <span className="text-xs text-gray-400">{t("sections_custom_color")}</span>
                    <code className="text-xs text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded">{form.color}</code>
                  </div>
                </div>
                {/* Description */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">{t("sections_description")}</label>
                  <input
                    value={form.description}
                    onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder={t("sections_optional")}
                  />
                </div>
                {/* Actions */}
                <div className="flex gap-2 pt-1">
                  <button type="submit" disabled={saving}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold py-2 rounded-xl disabled:opacity-50 transition">
                    {saving ? t("saving") : editingSection ? t("sections_save_changes") : t("sections_create_zone")}
                  </button>
                  <button type="button" onClick={cancelAction}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-500 rounded-xl transition">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ── Zone list sidebar (right, full height inside map) ── */}
          <div className="absolute top-3 right-3 bottom-3 z-10 flex flex-col bg-white rounded-2xl shadow-xl overflow-hidden"
            style={{ width: "280px" }}>
            {/* Sidebar header */}
            <div className="px-4 py-3 border-b bg-white flex-shrink-0">
              <p className="text-sm font-bold text-gray-800 mb-2">
                {t("sections_zones_label")} <span className="text-gray-400 font-normal">({sections.length})</span>
              </p>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder={t("sections_search_zones")}
                  className="w-full pl-8 pr-7 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            {/* Sidebar zone list */}
            <div className="flex-1 overflow-y-auto">
              {filteredSections.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-3 p-6 text-center">
                  {sections.length === 0 ? (
                    <>
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                        <MapIcon className="w-6 h-6 text-gray-300" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500">{t("sections_no_zones")}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{t("sections_draw_first")}</p>
                      </div>
                      <button onClick={startDrawing}
                        className="text-xs bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl font-semibold transition">
                        {t("sections_draw_first_btn")}
                      </button>
                    </>
                  ) : (
                    <p className="text-xs text-gray-400">No zones match "{searchQuery}"</p>
                  )}
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {filteredSections.map(section => (
                    <div key={section.id}
                      className={`group rounded-xl px-3 py-2.5 transition cursor-default ${
                        editingSection?.id === section.id
                          ? "bg-blue-50 border border-blue-200"
                          : "hover:bg-gray-50 border border-transparent"
                      }`}>
                      <div className="flex items-start gap-2.5">
                        <div className="w-3 h-3 rounded-full mt-0.5 flex-shrink-0"
                          style={{ backgroundColor: section.color || "#1A56A0" }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-800 truncate leading-tight">
                            {section.name}
                          </p>
                          {section.description && (
                            <p className="text-xs text-gray-400 truncate mt-0.5">{section.description}</p>
                          )}
                          <p className="text-xs text-gray-300 mt-0.5">
                            {section.polygon?.length || 0} {t("sections_pts")}
                          </p>
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition flex-shrink-0 mt-0.5">
                          <button
                            onClick={() => startEdit(section)}
                            className="p-1.5 rounded-lg bg-blue-50 text-blue-500 hover:bg-blue-100 transition"
                            title="Edit zone">
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => setConfirmDelete(section)}
                            className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition"
                            title="Delete zone">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar footer */}
            <div className="px-4 py-2.5 border-t bg-gray-50 flex-shrink-0">
              <p className="text-xs text-gray-400">
                {sections.length} {t("sections_zones_total")}
              </p>
            </div>
          </div>

        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      <Modal isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)} title={t("sections_delete_zone")} size="sm">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">
                Delete "{confirmDelete?.name}"?
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {t("sections_delete_confirm")}
              </p>
            </div>
          </div>
          <div className="flex gap-3 pt-2 border-t">
            <button onClick={() => handleDelete(confirmDelete.id)} disabled={deleting}
              className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition">
              {deleting ? t("deleting") : <><Trash2 className="w-4 h-4" /> {t("sections_delete_zone")}</>}
            </button>
            <button onClick={() => setConfirmDelete(null)} disabled={deleting}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg transition">
              {t("cancel")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default SectionsManager;
